// Move um meio de pagamento para outra credencial do mesmo banco.
//
// É a ação que consolida os duplicados: um merchant pode ter várias credenciais do mesmo
// processador que são a MESMA conta — nasceram assim porque cada meio de pagamento criava uma
// cópia. Movidos todos para uma, as outras ficam sem vínculo e podem ser apagadas.
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  useChangeConfigProcessor,
  useMerchantProcessors,
} from "@/services/admin/merchant-processors"
import type { MerchantProcessorLinkedConfig } from "@/types/admin-merchant-processor"

const prefix = "pages.adminMerchantProcessors"

type MoveConfigDialogProps = {
  /** Vínculo a mover; `null` fecha o diálogo. */
  config: MerchantProcessorLinkedConfig | null
  /** Credencial de origem — não pode ser destino de si mesma. */
  currentProcessorId: string
  /**
   * Banco da credencial de ORIGEM.
   *
   * Vem daqui, e não do vínculo, porque o vínculo não carrega banco: um meio de pagamento ligado
   * a esta credencial é, por definição, do banco dela. Antes o filtro comparava com um campo que
   * a API nunca enviou, então a lista de destinos vinha SEMPRE vazia.
   */
  gateway: string
  onOpenChange: (open: boolean) => void
}

export const MoveConfigDialog = ({
  config,
  currentProcessorId,
  gateway,
  onOpenChange,
}: MoveConfigDialogProps) => {
  const { t } = useTranslation()
  const [targetId, setTargetId] = useState("")
  const [moveFallback, setMoveFallback] = useState(false)

  const { data: processors, isLoading } =
    useMerchantProcessors()

  const { mutate, isPending } = useChangeConfigProcessor(
    () => {
      setTargetId("")
      setMoveFallback(false)
      onOpenChange(false)
    }
  )

  // Só credenciais do MESMO banco: o backend recusa outra coisa, e por bom
  // motivo — o método não sairia pelo processador que o atende.
  const targets = (processors ?? []).filter(
    (processor) =>
      processor.id !== currentProcessorId &&
      processor.gateway === gateway
  )

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setTargetId("")
      setMoveFallback(false)
    }
    onOpenChange(open)
  }

  return (
    <Dialog
      open={Boolean(config)}
      onOpenChange={handleOpenChange}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t(`${prefix}.move.title`)}
          </DialogTitle>
          <DialogDescription>
            {t(`${prefix}.move.description`)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t(`${prefix}.move.target`)}</Label>
            {isLoading ? (
              <Skeleton className="h-10 w-full rounded-md" />
            ) : targets.length === 0 ? (
              <p
                data-testid="move-config-empty"
                className="text-sm text-muted-foreground"
              >
                {t(`${prefix}.move.noTarget`, { gateway })}
              </p>
            ) : (
              <Select
                value={targetId}
                onValueChange={setTargetId}
              >
                <SelectTrigger
                  aria-label={t(`${prefix}.move.target`)}
                >
                  <SelectValue
                    placeholder={t(
                      `${prefix}.move.targetPlaceholder`
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {targets.map((processor) => (
                    <SelectItem
                      key={processor.id}
                      value={processor.id}
                    >
                      {processor.name ?? processor.gateway}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Só faz sentido oferecer quando a contingência é ESTA credencial:
              nos outros casos ela foi escolha independente, e arrastá-la
              trocaria o plano B sem o operador pedir. */}
          {config?.fallbackOnly === false && (
            <div className="flex items-start gap-2">
              <Checkbox
                id="move-fallback"
                checked={moveFallback}
                onCheckedChange={(checked) =>
                  setMoveFallback(checked === true)
                }
              />
              <Label
                htmlFor="move-fallback"
                className="text-sm font-normal leading-tight"
              >
                {t(`${prefix}.move.moveFallback`)}
              </Label>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
          >
            {t(`${prefix}.move.cancel`)}
          </Button>
          <Button
            disabled={!targetId || isPending}
            onClick={() => {
              if (!config) return
              mutate({
                configId: config.configId,
                processorId: targetId,
                moveFallback,
              })
            }}
          >
            {t(`${prefix}.move.confirm`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
